//******************************************************************************
// 点群PNGファイルを取得してDeck.glのPointCloudLayer用データを作成するためのクラス

//******************************************************************************
// import
//------------------------------------------------------------------------------
// maplibre
import MapLibreGL from "maplibre-gl";
//------------------------------------------------------------------------------
// 2点間距離を求める関数
import { distanceTo } from "./distanceTo";
//------------------------------------------------------------------------------
// TransformFeedback処理クラス
import { LumaTransform } from "./LumaTransform";
//------------------------------------------------------------------------------
// 点群PNG処理クラス
// import PointCloudPng from "./PointCloudPng";
//------------------------------------------------------------------------------
// TransformFeedback用頂点シェーダ
import vTranslate from "./translate.vs.glsl";
//------------------------------------------------------------------------------
// 「npm link」しただけなので要注意
import { FileFetcher } from "file-fetcher";
import { PointCloudPngParser } from "datapng-parser";

//******************************************************************************
// export main class
export class PcpngOptimizer {
	//**************************************************************************
	/**
	 * コンストラクタ
	 * @param {string} tileUrlTemplate
	 */
	constructor(tileUrlTemplate) {
		//----------------------------------------------------------------------
		// メンバ変数にタイルURLテンプレートを格納
		this.tileUrlTemplate = tileUrlTemplate;
		//----------------------------------------------------------------------
		// TransformFeedbackインスタンスを生成しておく
		this.tfManager = new LumaTransform().prepare(vTranslate, {
			vPosition: 3,
			// vColor: 4,
		});
		//----------------------------------------------------------------------
		// タイルフェッチツールインスタンスを生成しておく
		this.fileFetcher = new FileFetcher();
	}

	//**************************************************************************
	/**
	 * このメソッドを呼び出して結果を取得する
	 * @param {{x:number, y:number, z:number}} coords pcpng file coordinates
	 * @param {string} mimeType mimeType of source tile
	 * @returns {Promise<Uint8Array|Uint8ClampedArray|false>} RGBA array or false
	 * @throws {Error}
	*/
	async optimize(coords, mimeType) {
		//----------------------------------------------------------------------
		// try/catch処理
		try {
			// 点群PNGファイルを取得、処理して結果を返す
			// const img = await this.load(path);
			// return this.analyze(img);
			const path = Object.keys(coords).reduce((acc, cur) => {
				return acc.replace(`{${cur}}`, `${coords[cur]}`);
			}, this.tileUrlTemplate);
			const imageData = await this.fileFetcher.getImageData(path, mimeType);
			return this.analyze(imageData.data);
		} catch (error) {
			// 例外時はfalseを返す
			return false;
		}
	}

	//**************************************************************************
	/**
	 * 点群PNGファイルからDeck.glのPointCloudLayer用データを作成
	 * @param {Uint8Arrat|Uint8ClampedArray} pixelData RGBA array
	 * @returns {Array}
	*/
	async analyze(pixelData) {
		const parser = new PointCloudPngParser().parse(pixelData);
		const recordCount = parser.find("recordCount").value;
		const webmerDegree = parser.find("subType").value[2];
		const columns = {};
		parser.find("columns").value.forEach((column) => {
			const name = column.getName();
			if (typeof name !== "undefined") {
				columns[name] = {
					offset: column.getOffset(),
					factor: column.getFactor(),
					records: column.getRawRecords()
				};
			}
		});
		//----------------------------------------------------------------------
		// 原点整理
		// 世界サイズ
		const worldSize = 2 ** webmerDegree;
		// 原点の世界座標と経緯度
		const originXY = new MapLibreGL.MercatorCoordinate(
			columns.x.offset / worldSize,
			columns.y.offset / worldSize
		);
		const originLngLat = originXY.toLngLat();
		//----------------------------------------------------------------------
		// uniform変数処理
		// 2点間距離計算処理の上書き
		originLngLat.distanceTo = distanceTo;
		// 1ピクセルの長さ（メートル）を決める（経度方向と緯度方向それぞれ）
		const pixelLengthX = originLngLat.distanceTo(
			new MapLibreGL.MercatorCoordinate(
				originXY.x + 1 / worldSize, originXY.y
			).toLngLat()
		);
		const pixelLengthY = originLngLat.distanceTo(
			new MapLibreGL.MercatorCoordinate(
				originXY.x, originXY.y + 1 / worldSize
			).toLngLat()
		);
		// 上書きしたメソッドを破棄
		delete (originLngLat.distanceTo);
		// 高さのメートル換算係数を決める
		const factorZ = 10 ** (columns.z.factor * -1);
		//----------------------------------------------------------------------
		// 値を決める
		const values = await this.tfManager.assign({
			// aX: new Float32Array( columns.x.records ),
			// aY: new Float32Array( columns.y.records ),
			// aZ: new Float32Array( columns.z.records ),
			// aColor: new Float32Array( columns.color.records ),
			aX: { layout: { format: "uint8x4" }, data: columns.x.records },
			aY: { layout: { format: "uint8x4" }, data: columns.y.records },
			aZ: { layout: { format: "uint8x4" }, data: columns.z.records },
			// aColor: new Float32Array( columns.color.records ),
		}, recordCount).run({
			uFactorX: pixelLengthX,
			uFactorY: pixelLengthY,
			uFactorZ: factorZ
		});
		// 不要になったバッファを破棄
		// tfManager.dispose();
		// 点群PNG解析結果を破棄
		// pcpng.dispose();
		// 点群PNGタイル元データをガベージコレクションにのせる
		parser.dispose();
		//----------------------------------------------------------------------
		// 結果を返す
		/*
			PointCloudLayerで必要な値
				原点座標（経緯度）
				各ポイントのオフセットと色
				ポイント数
			座標復元で必要な値
				原点座標（ポイント座標）
				ウェブメルカトル次数
				1ピクセルの距離（x）
				1ピクセルの距離（y）
		*/
		return {
			//------------------------------------------------------------------
			// 原点経緯度、ポイントデータ、ポイント数は点群展開用
			origin: originLngLat,
			data: { vPosition: values.vPosition, vColor: columns.color.records },
			count: recordCount,
			//------------------------------------------------------------------
			// ウェブメルカトル次数、オフセット、ピクセルの長さは位置座標復元用
			pcpng: {
				webmercatorDegree: webmerDegree,
				offset: {
					x: columns.x.offset,
					y: columns.y.offset,
					z: columns.z.offset
				},
				meterPerPixel: {
					x: pixelLengthX,
					y: pixelLengthY,
				},
			}
		}
	}
}
